/**
 * Site Performance API — supplier-site monthly OTD scorecard.
 *
 * Read-only and supplier-sites-only (the backend returns an empty set for
 * customer views). `otd_pct` values arrive as decimal strings (DRF) — parse
 * with parseFloat at the point of use.
 */
import api, { type AxiosResponse } from './client';

export interface SiteOtdCustomerRow {
  customer_id: number;
  customer_name: string;
  customer_code: string;
  measurable: number;
  on_time: number;
  early: number;
  late: number;
  otd_pct: string | null;
}

export interface SiteOtdMonthRow {
  id: number;
  site_id: number;
  site_name: string;
  /** First day of the month, 'YYYY-MM-DD'. */
  period_month: string;
  /** Human label, e.g. 'May 2026'. */
  label: string;
  /** True once frozen; false = in-progress current month (refreshed nightly). */
  is_closed: boolean;
  closed_at: string | null;
  last_captured_at: string | null;
  grace_days: number;
  measurable: number;
  on_time: number;
  early: number;
  late: number;
  otd_pct: string | null;
}

export interface SiteOtdThresholds {
  excellent: number;
  good: number;
  poor: number;
}

export interface SiteOtdMonthDetail extends SiteOtdMonthRow {
  customers: SiteOtdCustomerRow[];
  thresholds: SiteOtdThresholds;
}

export interface SiteOtdListParams {
  /** Restrict to one accessible site. */
  site?: number;
  /** Trailing months (default 13, max 36). */
  months?: number;
}

export interface SiteOtdMatrixCustomer {
  customer_id: number;
  customer_name: string;
  customer_code: string;
  /** OTD % per month, keyed by 'YYYY-MM-DD' (period_month). null = no data. */
  scores: Record<string, number | null>;
}

export interface SiteOtdMatrix {
  site_id: number | null;
  site_name: string | null;
  /** Latest month first (left-to-right in the grid). */
  months: { period_month: string; label: string }[];
  month_keys: string[];
  customers: SiteOtdMatrixCustomer[];
}

export interface SiteOtdMissLine {
  line_id: number;
  po_number: string | null;
  part_number: string;
  customer_id: number | null;
  customer_name: string;
  /** Supplier Promise (due) date, 'YYYY-MM-DD'. */
  promised_date: string | null;
  /** Actual dispatch date, 'YYYY-MM-DD'. */
  dispatched_date: string | null;
  /** dispatched − promised, in days (positive = late). null if not dispatched. */
  days_late: number | null;
  verdict: 'late' | 'early' | 'on_time' | string | null;
  overridden: boolean;
  override_value: boolean | null;
  override_reason: string;
}

export interface SiteOtdMissSummary {
  measurable: number;
  on_time: number;
  early: number;
  late: number;
  otd_pct: number | null;
}

export interface SiteOtdMissResponse {
  site_id: number;
  month: string;
  customer_id: number | null;
  summary: SiteOtdMissSummary;
  /** Present on the misses read; the toggle for the override button. */
  can_override?: boolean;
  lines: SiteOtdMissLine[];
}

export const performanceAPI = {
  /** Monthly OTD headers across the user's accessible supplier sites. */
  listSiteOtd(
    params?: SiteOtdListParams,
  ): Promise<AxiosResponse<SiteOtdMonthRow[]>> {
    return api.get('/performance/site-otd/', { params });
  },

  /** One site-month with its per-customer OTD breakdown + RAG thresholds. */
  getSiteOtdMonth(id: number): Promise<AxiosResponse<SiteOtdMonthDetail>> {
    return api.get(`/performance/site-otd/${id}/`);
  },

  /** Rolling N-month OTD matrix (customers × months) for one site. */
  getSiteOtdMatrix(
    params: { site?: number; months?: number },
  ): Promise<AxiosResponse<SiteOtdMatrix>> {
    return api.get('/performance/site-otd/matrix/', { params });
  },

  /** Late deliveries behind a sub-100 OTD cell (site-month, optionally one customer). */
  getSiteOtdMisses(
    params: { site: number; month: string; customer?: number },
  ): Promise<AxiosResponse<SiteOtdMissResponse>> {
    return api.get('/performance/site-otd/misses/', { params });
  },

  /** Override one late line (mark on-time / clear), recompute the cell, return refreshed misses. */
  overrideOtdLine(body: {
    line_id: number;
    overridden: boolean;
    override_value?: boolean | null;
    reason?: string;
    /** Modal scope — pass the customer id for a customer cell; omit for site-wide. */
    customer?: number | null;
  }): Promise<AxiosResponse<SiteOtdMissResponse>> {
    return api.post('/performance/site-otd/override-line/', body);
  },
};
